import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import React from "react";
import ReactDOM from "react-dom";
import { AppTheme } from "./AppTheme";

/**
 * React DOM proxy class, used to centralise the rendering and unmounting of React components.
 */
export class ReactDOMProxy {

	private static readonly componentClassName = "react-component";

	/**
	 * Renders a styled component to a container element by wrapping it with a ThemeProvider and a StyledEngineProvider.
	 * @param element The component to render.
	 * @param container The container to render the component to.
	 * @param callback Callback which is called after the component has been rendered.
	 */
	public static renderStyled(element: JSX.Element, container?: Element, callback?: () => void) {

		const styledElement =
			<StyledEngineProvider injectFirst>
				<ThemeProvider theme={AppTheme}>
					{element}
				</ThemeProvider>
			</StyledEngineProvider>;
		this.render(styledElement, container ?? document.createElement("div"), callback);
	}

	/**
	 * Renders a component to a container element.
	 * @param element The component to render.
	 * @param container The container to render the component to.
	 * @param callback Callback which is called after the component has been rendered.
	 */
    public static render(element: JSX.Element, container: Element, callback?: () => void) {

		ReactDOM.render(element, container, callback);
        container.classList.add(ReactDOMProxy.componentClassName);
    }

	/**
	 * Unmounts all React components within a container element.
	 * @param container A container element to search.
	 */
    public static unmountAll(container: Element) {

        container.querySelectorAll(`.${ReactDOMProxy.componentClassName}`).forEach(element => {

            ReactDOMProxy.unmount(element);
            element.classList.remove(ReactDOMProxy.componentClassName);
        });
    }

	/**
	 * Unmounts a component.
	 * @param container The container the component was rendered to.
	 */
    public static unmount(container: Element) {

        ReactDOM.unmountComponentAtNode(container);
    }
}